import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import EmojiPicker, { EmojiStyle, Theme, SuggestionMode } from 'emoji-picker-react';

const PICKER_WIDTH = 350;
const PICKER_HEIGHT = 420;
const VIEWPORT_GUTTER = 12;

export const EmojiPickerPopover = ({ open, onSelectEmoji, onClose, anchorRef }) => {
  const popoverRef = useRef(null);
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) {
      setPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      const width = Math.min(PICKER_WIDTH, window.innerWidth - VIEWPORT_GUTTER * 2);
      const height = Math.min(PICKER_HEIGHT, window.innerHeight - VIEWPORT_GUTTER * 2);
      const left = Math.min(
        Math.max(VIEWPORT_GUTTER, rect.right - width),
        window.innerWidth - width - VIEWPORT_GUTTER
      );
      const top = Math.max(
        VIEWPORT_GUTTER,
        Math.min(
          (window.innerHeight - height) / 2,
          window.innerHeight - height - VIEWPORT_GUTTER
        )
      );

      setPosition({ top, left, width, height });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorRef, open]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      if (
        popoverRef.current
        && !popoverRef.current.contains(event.target)
        && !anchorRef?.current?.contains(event.target)
      ) {
        onClose();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [anchorRef, open, onClose]);

  if (!open || !position) return null;

  return createPortal(
    <div
      ref={popoverRef}
      className="fixed z-[1200] overflow-hidden rounded-2xl border border-zinc-200/90 bg-white shadow-2xl animate-in zoom-in-95 duration-150"
      style={{ top: position.top, left: position.left, width: position.width }}
    >
      <EmojiPicker
        onEmojiClick={(emojiData, event) => {
          const emoji = emojiData?.emoji || '';
          if (emoji) {
            onSelectEmoji(emoji);
          }
        }}
        autoFocusSearch={false}
        lazyLoadEmojis={true}
        emojiStyle={EmojiStyle.APPLE}
        theme={Theme.LIGHT}
        suggestedEmojisMode={SuggestionMode.FREQUENT}
        searchPlaceHolder="Kërko midis mijëra emotikonash..."
        previewConfig={{
          showPreview: true,
          defaultEmoji: '1f60a',
          defaultCaption: 'Zgjidhni një emotikon...',
        }}
        skinTonesDisabled={false}
        searchDisabled={false}
        height={position.height}
        width={position.width}
      />
    </div>,
    document.body
  );
};

export default EmojiPickerPopover;
